/**
 * Google reCAPTCHA v3 helper.
 *
 * Lazily loads the reCAPTCHA script and returns a per-action token to send
 * with form submissions (as the `X-Recaptcha-Token` header). When no site key
 * is configured, getRecaptchaToken() resolves to null and callers proceed
 * normally — the backend skips verification in that case too.
 */
const SITE_KEY = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY || "";

let scriptPromise = null;

function loadScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Get a reCAPTCHA v3 token for an action (e.g. "submit_booking").
 * Returns null when reCAPTCHA is not configured or fails to load.
 */
export async function getRecaptchaToken(action) {
  if (!SITE_KEY) return null;
  try {
    await loadScript();
    await new Promise((resolve) => window.grecaptcha.ready(resolve));
    return await window.grecaptcha.execute(SITE_KEY, { action });
  } catch {
    return null;
  }
}

/** Build request headers carrying the token, or {} when there is none. */
export async function recaptchaHeaders(action) {
  const token = await getRecaptchaToken(action);
  return token ? { "X-Recaptcha-Token": token } : {};
}
